// Component: Tabs.jsx
// Purpose: DS standard for section navigation. Two variants:
//          · "underline" (DEFAULT) — page section nav; understated fg-1 label
//            with a single accent indicator that GLIDES between tabs. Use for
//            detail-page sections.
//          · "pill" — soft accent-tinted pills; use ONLY for 2–4 view/filter
//            toggles, so tabs don't compete with pill chips inside content.
//          Replaces the ad-hoc per-page MUI <Tabs> and custom button-tabs
//          (each had its own blue/indigo accent).
//
// Value model: each tab's identity is `tab.value ?? index`. So it drops into
//   BOTH index-based state (`activeTab` = 0..n, tabs have no `value`) and
//   key-based state (`activeTab` = "orders", tabs carry `value: "orders"`).
//   onChange returns that same identity.
//
// Usage:
//   <Tabs value={activeTab} onChange={setActiveTab}
//     tabs={[{ label: "Details" }, { label: "Orders", badge: 12, icon: <I/> }]} />

import React, { useLayoutEffect, useRef, useState } from "react";

export const Tabs = ({ tabs = [], value = 0, onChange, variant = "underline", className = "" }) => {
  const listRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  // Position the sliding underline under the active tab. Runs after layout so
  // the measured box already reflects the active tab's (bolder) width.
  useLayoutEffect(() => {
    if (variant !== "underline") return;
    const measure = () => {
      const el = listRef.current?.querySelector(".ds-tab.is-active");
      if (el) setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value, variant, tabs]);

  if (!tabs.length) return null;

  return (
    <div ref={listRef} role="tablist" className={`ds-tabs ds-tabs--${variant} ${className}`}>
      {tabs.map((tab, i) => {
        const key = tab.value ?? i;
        const isActive = value === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={tab.testId}
            className={`ds-tab ${isActive ? "is-active" : ""}`}
            onClick={() => onChange?.(key)}
          >
            {tab.icon}
            {tab.label}
            {tab.badge != null && <span className="ds-tab-badge">{tab.badge}</span>}
          </button>
        );
      })}
      {variant === "underline" && (
        <span
          className="ds-tab-indicator"
          aria-hidden="true"
          style={{ left: indicator.left, width: indicator.width }}
        />
      )}
    </div>
  );
};

export default Tabs;
